// Domain models for the Devices section. Mirrors the shape the backend
// `devices` query is expected to return once it exists; the client gates the
// whole section on the MANAGE_DEVICES capability (see lib/capabilities.ts) and
// the API must independently enforce that capability server-side.

export type DeviceStatus = 'ONLINE' | 'OFFLINE' | 'PENDING';

// Camera connection states:
//   ONLINE   — reachable and delivering frames
//   OFFLINE  — not reachable over the network
//   PENDING  — newly added / not yet contacted
//   ERROR    — reachable over the network, but authentication or image
//              transmission failed (see `errorKind` for which)
export type CameraStatus = 'ONLINE' | 'OFFLINE' | 'PENDING' | 'ERROR';

// Disambiguates an ERROR camera: bad/expired credentials vs. a stream that
// connected but failed to deliver a usable frame.
export type CameraErrorKind = 'AUTH' | 'TRANSMISSION';

export interface Camera {
  id: string;
  // Human label for the cooking station this camera watches (e.g. "Grill Cam").
  name: string;
  status: CameraStatus;
  // Latest still frame from the camera. `null` when no frame is available yet
  // (newly added, offline, or still processing) — the UI renders an empty state.
  imageUrl: string | null;
  // Only meaningful when `status === 'ERROR'`. Defaults to AUTH if omitted.
  errorKind?: CameraErrorKind;
}

export interface Device {
  // Kit ID — short human-facing device id (5 alphanumeric chars, e.g. "67W59").
  id: string;
  // Full hardware serial number (alphanumeric, e.g. "9427C253201640").
  serial: string;
  status: DeviceStatus;
  // True while a ping round-trip is in flight server-side. Set by `pingDevice`
  // and cleared once the device has answered (or timed out) and its camera
  // frames have settled. The client keeps the ping button disabled and polls
  // until this flips back to false. See device-data-backend.md.
  pinging: boolean;
  // Pre-formatted "last seen just now" / "last reachable 2h ago" style label.
  // This tracks the last heartbeat / communication with the device, not the
  // last ping (a ping is a heavier round-trip that also pulls camera frames).
  lastHeartbeatLabel: string;
  // Location this device is installed at, or `null` for an unpaired device
  // that has been provisioned but not yet associated with a location.
  locationName: string | null;
  // Cameras attached to this device. Always empty for unpaired devices.
  cameras: Camera[];
}

export function isUnpaired(device: Device): boolean {
  return device.locationName === null;
}

// Title shown in the card header: the location name for a paired device, or a
// serial-bearing descriptor for an unpaired one.
export function deviceTitle(device: Device): string {
  if (isUnpaired(device)) {
    return `Device: ${device.id} (serial number: ${device.serial})`;
  }
  return device.locationName as string;
}

// Title + subtext for the camera ERROR empty state. The device answered on the
// network, so this is deliberately distinct from the OFFLINE ("no signal") copy.
export function cameraErrorCopy(camera: Camera): { title: string; sub: string } {
  if (camera.errorKind === 'TRANSMISSION') {
    return { title: 'Transmission failed', sub: 'Reachable · stream interrupted' };
  }
  return { title: 'Authentication failed', sub: 'Reachable · credentials rejected' };
}
